import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, useTheme } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { LogoUpload } from '../../../components/common/LogoUpload';
import { useAppDispatch } from '../../../../application/store/useStore';
import { updateCompanyAsync } from '../../../../application/features/company/companyThunk';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
export function EditCompanyDialog({ openEditCompanyDialog, onCloseEditCompany, companyToEdit, onEditCompany }) {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [logoBase64, setLogoBase64] = useState(null);
    const dispatch = useAppDispatch();
    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            name: '',
            address: '',
            companyDomain: '',
            email: '',
            phoneNumber: '',
            description: ''
        }
    });
    const requiredMessage = t('requiredField');
    const theme = useTheme();
    // Effect to populate form when companyToEdit changes or dialog opens
    useEffect(() => {
        if (openEditCompanyDialog && companyToEdit) {
            reset({
                name: companyToEdit.name || '',
                address: companyToEdit.address || '',
                companyDomain: companyToEdit.companyDomain || '',
                email: companyToEdit.email || '',
                phoneNumber: companyToEdit.phoneNumber || '',
                description: companyToEdit.description || ''
            });
            setLogoBase64(companyToEdit.logo || null);
        }
        else if (!openEditCompanyDialog) {
            // Reset form when dialog closes
            reset({
                name: '',
                address: '',
                companyDomain: '',
                email: '',
                phoneNumber: '',
                description: ''
            });
            setLogoBase64(null);
        }
    }, [openEditCompanyDialog, companyToEdit, reset]);
    // Form submission handler
    const onSubmit = async (formData) => {
        if (!companyToEdit?.id) {
            toast.error(t('noCompanySelected'));
            return;
        }
        setIsSubmitting(true);
        try {
            const updateData = {
                ...formData,
                id: companyToEdit.id,
                logoBase64: logoBase64 || undefined
            };
            const result = await dispatch(updateCompanyAsync(updateData)).unwrap();
            toast.success(t('companyUpdatedSuccess'));
            onEditCompany(result);
            onCloseEditCompany();
        }
        catch (error) {
            toast.error(t('companyUpdateFailed'));
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleClose = () => {
        if (!isSubmitting) {
            onCloseEditCompany();
        }
    };
    return (_jsxs(Dialog, { maxWidth: 'sm', fullWidth: true, sx: {
            '& .MuiDialog-paper': {
                overflow: 'hidden'
            }
        }, open: openEditCompanyDialog, onClose: handleClose, children: [_jsxs(DialogTitle, { sx: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    zIndex: 1000,
                    padding: '16px'
                }, children: [t('editCompany'), _jsx(IconButton, { "aria-label": 'close', onClick: handleClose, disabled: isSubmitting, sx: {
                            color: theme.palette.action.active
                        }, children: _jsx(CloseOutlined, {}) })] }), _jsx(DialogContent, { sx: {
                    overflowY: 'auto'
                }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '20px', children: [_jsx(LogoUpload, { value: logoBase64, onChange: setLogoBase64 }), _jsx(Box, { display: 'flex', flexDirection: 'column', gap: '12px', children: _jsxs(Grid, { container: true, spacing: 1.5, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsx(Controller, { name: 'name', control: control, rules: {
                                                required: {
                                                    value: true,
                                                    message: requiredMessage
                                                }
                                            }, render: ({ field }) => (_jsx(TextField, { fullWidth: true, ...field, label: t('name'), disabled: isSubmitting, error: !!errors.name, helperText: errors.name?.message })) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Controller, { name: 'address', control: control, render: ({ field }) => (_jsx(TextField, { fullWidth: true, ...field, label: t('address'), disabled: isSubmitting, error: !!errors.address, helperText: errors.address?.message })) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Controller, { name: 'companyDomain', control: control, render: ({ field }) => (_jsx(TextField, { fullWidth: true, ...field, label: t('domain'), disabled: isSubmitting, error: !!errors.companyDomain, helperText: errors.companyDomain?.message })) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Controller, { name: 'email', control: control, rules: {
                                                required: {
                                                    value: true,
                                                    message: requiredMessage
                                                },
                                                pattern: {
                                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                    message: t('invalidEmail')
                                                }
                                            }, render: ({ field }) => (_jsx(TextField, { fullWidth: true, ...field, label: t('email'), disabled: isSubmitting, error: !!errors.email, helperText: errors.email?.message })) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Controller, { name: 'phoneNumber', control: control, render: ({ field }) => (_jsx(TextField, { fullWidth: true, ...field, label: t('phoneNumber'), disabled: isSubmitting, error: !!errors.phoneNumber, helperText: errors.phoneNumber?.message })) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Controller, { name: 'description', control: control, render: ({ field }) => (_jsx(TextField, { fullWidth: true, ...field, label: t('description'), disabled: isSubmitting, multiline: true, rows: 4, error: !!errors.description, helperText: errors.description?.message })) }) })] }) })] }) }), _jsxs(DialogActions, { sx: {
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'white',
                    zIndex: 1000,
                    padding: '16px'
                }, children: [_jsx(Button, { onClick: handleClose, disabled: isSubmitting, variant: 'outlined', children: t('cancel') }), _jsx(Button, { onClick: handleSubmit(onSubmit), disabled: isSubmitting, variant: 'contained', color: 'primary', children: isSubmitting ? t('updating') : t('editCompany') })] })] }));
}
